import { GlobalSearch } from '@/features/search/GlobalSearch';
import { type ComponentType, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Menu, Moon, Sun, type LucideProps } from 'lucide-react';
import { IconButton } from '@/components/ui';
import { useTheme } from '@/lib/theme';
import { HeaderHelpLink, HeaderProductName } from '@/lib/platformAppearance';
import { FontSizeMenu } from './FontSizeMenu';
import { UserMenu } from './UserMenu';

export interface HeaderNavGroup {
  label?: string;
  items: Array<{ to: string; label: string; icon: ComponentType<LucideProps>; keywords?: string; end?: boolean }>;
}

/** Every role supplies its existing permission-filtered navigation. */
export function AppHeader({ desktop, groups, onToggleNavigation, navigationExpanded, navigateTo, tools, workspaceControl, context = 'Workspace' }: {
  desktop: boolean;
  groups: HeaderNavGroup[];
  onToggleNavigation: () => void;
  navigationExpanded?: boolean;
  navigateTo?: (to: string) => void;
  tools?: ReactNode;
  workspaceControl?: ReactNode;
  context?: string;
}) {
  const { pathname } = useLocation();
  const { resolved, setPreference } = useTheme();
  const current = groups.flatMap(g => g.items).filter(i => i.end ? pathname === i.to : pathname === i.to || pathname.startsWith(i.to + '/')).sort((a, b) => b.to.length - a.to.length)[0];
  return <>
    <header className="topbar app-header">
      <IconButton className="app-nav-toggle" label={desktop ? 'Toggle sidebar' : 'Open navigation'} icon={<Menu size={20} />} onClick={onToggleNavigation} aria-expanded={navigationExpanded} />
      {workspaceControl}
      <div className="app-header-location"><span>{context}</span><strong>{current?.label ?? 'Account'}</strong></div>
      <GlobalSearch groups={groups} navigateTo={navigateTo} />
      <div className="topbar-actions">
        <span className="app-product-name"><HeaderProductName /></span>
        <span className="app-header-help"><HeaderHelpLink /></span>
        <FontSizeMenu />
        <IconButton className="app-theme-toggle" label={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} theme`} icon={resolved === 'dark' ? <Sun size={20} /> : <Moon size={20} />} onClick={() => setPreference(resolved === 'dark' ? 'light' : 'dark')} />
        {tools}
        <div className="app-header-user pf-header-user"><UserMenu variant={desktop ? 'chip' : 'avatar'} /></div>
      </div>
    </header>

  </>;
}
